import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import { Sidenote, SidenoteLayout } from '../components/ui';

type Mode = 'signin' | 'signup' | 'magic';

export default function LoginPage() {
  const { t } = useTranslation();
  const { user, configured, signInWithEmail, signUpWithEmail, signInWithOtp } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const redirect = params.get('redirect') || '/library';

  if (user) return <Navigate to={redirect} replace />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    const fn =
      mode === 'signin'
        ? () => signInWithEmail(email, password)
        : mode === 'signup'
        ? () => signUpWithEmail(email, password)
        : () => signInWithOtp(email);
    const { error: err } = await fn();
    if (err) setError(err);
    else if (mode === 'signup') setMessage(t('auth.checkEmail'));
    else if (mode === 'magic') setMessage(t('auth.magicSent'));
    setSubmitting(false);
  }

  const title =
    mode === 'signup' ? t('auth.signupTitle')
    : mode === 'magic' ? t('auth.magicTitle')
    : t('auth.signinTitle');

  const submitLabel = submitting
    ? t('auth.working')
    : mode === 'signup' ? t('auth.signup')
    : mode === 'magic' ? t('auth.sendMagic')
    : t('auth.signin');

  return (
    <main className="container auth-page">
      <SidenoteLayout>
        <SidenoteLayout.Main>
          <h1>{title}</h1>
          {!configured && (
            <p className="auth-banner-warn" role="alert">
              {t('auth.notConfiguredBanner')}
            </p>
          )}
          <form onSubmit={onSubmit} className="auth-form">
            <label htmlFor="auth-email">
              {t('auth.email')}
              <input
                id="auth-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            {mode !== 'magic' && (
              <label htmlFor="auth-password">
                {t('auth.password')}
                <input
                  id="auth-password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                />
              </label>
            )}
            {error && <p className="auth-error" role="alert">{error}</p>}
            {message && <p className="auth-message" role="status">{message}</p>}
            <button type="submit" disabled={submitting || !configured} className="btn btn-accent">
              {submitLabel}
            </button>
          </form>

          <div className="auth-mode-switch">
            {mode !== 'signin' && (
              <button type="button" onClick={() => setMode('signin')}>
                {t('auth.switchToSignin')}
              </button>
            )}
            {mode !== 'signup' && (
              <button type="button" onClick={() => setMode('signup')}>
                {t('auth.switchToSignup')}
              </button>
            )}
            {mode !== 'magic' && (
              <button type="button" onClick={() => setMode('magic')}>
                {t('auth.switchToMagic')}
              </button>
            )}
          </div>
        </SidenoteLayout.Main>

        <SidenoteLayout.Aside>
          <Sidenote label={t('auth.aside.whyLabel')} marker="★">
            <p>{t('auth.aside.why1')}</p>
            <p>{t('auth.aside.why2')}</p>
            <p>{t('auth.aside.why3')}</p>
          </Sidenote>
          <Sidenote label={t('auth.aside.privacyLabel')} tone="mute">
            <p>{t('auth.aside.privacyBody')}</p>
          </Sidenote>
        </SidenoteLayout.Aside>
      </SidenoteLayout>
    </main>
  );
}
